/**
 * Empty, loading & error states (screenshots/54, brief §2.9 "States"). No spinners, no skeletons: pages paint
 * from the snapshot, and these show only when there's genuinely nothing to show or something fails. Every
 * state says why and gives one next step, and the page around it keeps working.
 *
 * Where each one goes (builders B/C drop these into their pages):
 *   1 NotEnoughHistory   in place of a ForecastMeter when a member has < 3 similar past votes (influence table, member rows)
 *   2 NoLobbying         Legislation detail · "Who's lobbying" / Lobbying & money tab when no client has filed
 *   3 NoWard             resident pages that need the ward (People · Your alderperson, Legislation side column)
 *   4 ReconnectingToast  the shell (any page with a live channel)
 *   5 AllCaughtUp        Meetings · Following when nothing changed on your follows
 *   6 NoResults          Following search, ⌘K, any list search
 *   7 TranscriptNotReady meeting transcript panel while it's being transcribed
 *   8 TabErrorBoundary   around every tab's content (meeting page, legislation detail, profile tabs)
 *   9 NotAvailable       a tab the user's group doesn't have (opened by URL)
 *   EmptyState           any other "nothing here" (the starter API: icon name, title, body, one action)
 * One implementation: the handoff starter's EmptyState / NotEnoughHistory / TabErrorBoundary / Locked API,
 * drawn with lucide icons and the 54 measurements.
 */
import { Component, useEffect, useRef, type ErrorInfo, type ReactNode } from "react";
import { AlertTriangle, Bell, Building2, CalendarPlus, CheckCheck, ExternalLink, Info, Lock, MapPin, RefreshCw, Loader, Search, SearchX, WifiOff, type LucideIcon } from "lucide-react";
import { useShallow } from "zustand/react/shallow";
import { connectionStore, useConnection } from "../../stores/connectionStore";
import { duration, easing, prefersReducedMotion, reducedFallback } from "../../motion";
import { navigate } from "../../app/router";

/* ---------- the shared card ---------- */

export function StateCard({ icon, tone = "neutral", title, children, actions, role }: {
  icon: ReactNode; tone?: "neutral" | "negative"; title: ReactNode; children?: ReactNode; actions?: ReactNode; role?: "alert" | "status";
}) {
  return (
    <div className="state-card" data-tone={tone} role={role}>
      <span className="state-card__icon" aria-hidden>{icon}</span>
      <h3 className="state-card__title">{title}</h3>
      {children && <p className="state-card__body">{children}</p>}
      {actions && <div className="state-card__actions">{actions}</div>}
    </div>
  );
}

const Action = ({ icon, children, onClick, href }: { icon: ReactNode; children: ReactNode; onClick?: () => void; href?: string }) =>
  href ? <a className="state-btn" href={href} target="_blank" rel="noreferrer">{icon}{children}</a>
    : <button className="state-btn" onClick={onClick}>{icon}{children}</button>;

/* 1 · Not enough history: shown in place of the meter. */
export const NotEnoughHistory = () => (
  <span className="state-chip" title="We forecast after 3 similar votes." tabIndex={0} aria-label="Not enough history. We forecast after 3 similar votes.">Not enough history</span>
);

/* 2 · No lobbying on this item: never hide the section. */
export const NoLobbying = ({ onAlert }: { onAlert: () => void }) => (
  <StateCard icon={<Building2 size={18} />} title="No registered lobbying on this item yet"
    actions={<Action icon={<Bell size={14} />} onClick={onAlert}>Alert me if someone files</Action>}>
    We check Board of Ethics filings every day and will add clients the day they file.
  </StateCard>
);

/* 3 · Resident without a ward: everything that needs the ward waits behind one step. */
export const NoWard = ({ onFind }: { onFind?: () => void }) => (
  <StateCard icon={<MapPin size={18} />} title="Add your address to see your alderperson"
    actions={<Action icon={<MapPin size={14} />} onClick={onFind ?? (() => navigate("/dashboard/welcome/ward"))}>Find my ward</Action>}>
    Used once to find your ward. Never shown or shared.
  </StateCard>
);

/* 4 · Live connection lost: the page keeps the last snapshot; this toast says so. */
let lastLiveAt: string | null = null;
connectionStore.subscribe((s) => { if (s.status === "live") lastLiveAt = new Date().toISOString(); });
const clock = (iso: string | null) => (iso ? new Date(iso).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" }) : "");

export function ReconnectingToast() {
  const { status, attempts, snapshotAt } = useConnection(useShallow((s) => ({ status: s.status, attempts: s.attempts, snapshotAt: s.snapshotAt })));
  const down = status === "reconnecting" || (status === "snapshot" && attempts > 0);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!down || !ref.current) return;
    ref.current.animate(prefersReducedMotion() ? [{ opacity: 0 }, { opacity: 1 }] : [{ opacity: 0, transform: "translateY(12px)" }, { opacity: 1, transform: "none" }],
      prefersReducedMotion() ? { duration: reducedFallback.duration } : { duration: duration.base, easing: easing.enter });
  }, [down]);
  if (!down) return null;
  // Retry now: the live client reconnects immediately on "online" (liveClient.ts reconnectNow).
  return (
    <div className="reconnect-toast" role="status" ref={ref}>
      <WifiOff size={16} className="reconnect-toast__icon" />
      <span className="toast__msg">Reconnecting. Showing data from {clock(lastLiveAt ?? snapshotAt)}.</span>
      <button className="reconnect-toast__retry" onClick={() => dispatchEvent(new Event("online"))}>Retry</button>
    </div>
  );
}

/* 5 · All caught up: celebrate briefly, then point to what's next. */
export const AllCaughtUp = ({ items, since, next, onAddToCalendar }: { items: number; since: string; next?: string | null; onAddToCalendar?: () => void }) => (
  <StateCard icon={<CheckCheck size={18} />} title="You're all caught up" role="status"
    actions={next && onAddToCalendar ? <Action icon={<CalendarPlus size={14} />} onClick={onAddToCalendar}>Add to calendar</Action> : undefined}>
    Nothing changed on your {items} items since {since}.{next ? ` Next: ${next}.` : ""}
  </StateCard>
);

/* 6 · No search results: say what we searched and offer a broader search. */
export const NoResults = ({ query, onBroaden }: { query: string; onBroaden?: () => void }) => (
  <StateCard icon={<SearchX size={18} />} title={`No results for "${query}"`} role="status"
    actions={onBroaden ? <Action icon={<Search size={14} />} onClick={onBroaden}>Search all years</Action> : undefined}>
    {onBroaden ? "Try fewer words, or search all years." : null}
  </StateCard>
);

/* 7 · Transcript not ready: the meeting and its actions show right away; moments fill in later. */
export const TranscriptNotReady = ({ status = true }: { status?: boolean }) => (
  <div className="state-transcript" role="status">
    {status && <span className="status status--processing"><Loader size={12} />Transcribing…</span>}
    <p>Moments will appear when the transcript is ready. We'll notify you.</p>
  </div>
);

/* 8 · A tab failed to load: only that tab errors; the header, tabs and side column keep working. */
export const TabError = ({ tab, reason, onRetry, sourceUrl, sourceLabel = "Open on City Clerk" }: {
  tab: string; reason?: string; onRetry: () => void; sourceUrl?: string | null; sourceLabel?: string;
}) => (
  <StateCard icon={<AlertTriangle size={18} />} tone="negative" title={`We couldn't load ${tab}`} role="alert"
    actions={<>
      <Action icon={<RefreshCw size={14} />} onClick={onRetry}>Try again</Action>
      {sourceUrl && <Action icon={<ExternalLink size={14} />} href={sourceUrl}>{sourceLabel}</Action>}
    </>}>
    {reason ? `${reason} ` : ""}Your other tabs are fine.
  </StateCard>
);

/** Wrap each tab's content: a render error shows TabError in that tab only; switching tabs or "Try again" clears it. */
export class TabErrorBoundary extends Component<{ tabLabel: string; reason?: string; fallbackHref?: string | null; fallbackLabel?: string; children: ReactNode }, { failed: boolean; key: number }> {
  state = { failed: false, key: 0 };
  static getDerivedStateFromError() { return { failed: true }; }
  componentDidCatch(error: Error, info: ErrorInfo) { console.error(`${this.props.tabLabel} tab`, error, info.componentStack); }
  componentDidUpdate(prev: { tabLabel: string }) { if (prev.tabLabel !== this.props.tabLabel && this.state.failed) this.setState((s) => ({ failed: false, key: s.key + 1 })); }
  render() {
    if (this.state.failed) {
      return (
        <div className="tab-panel tab-error">
          <TabError tab={this.props.tabLabel} reason={this.props.reason} sourceUrl={this.props.fallbackHref} sourceLabel={this.props.fallbackLabel}
            onRetry={() => this.setState((s) => ({ failed: false, key: s.key + 1 }))} />
        </div>
      );
    }
    return <TabKey k={this.state.key}>{this.props.children}</TabKey>;
  }
}
const TabKey = ({ k, children }: { k: number; children: ReactNode }) => <div key={k} className="tab-boundary">{children}</div>;

/* 9 · Not available for your group: says who it's for, with no upsell. */
export const NOT_AVAILABLE: Partial<Record<string, { title: string; body: string; action: string; href: string }>> = {
  analytics: { title: "Analytics is for offices and advocacy groups", body: "Residents get forecasts, votes and money on every bill and profile.",
    action: "See what residents get", href: "/dashboard/legislation" },
};

export function NotAvailable({ title, body, action, href }: { title: string; body?: string; action?: string; href?: string }) {
  return (
    <StateCard icon={<Lock size={18} />} title={title}
      actions={action && href ? <a className="state-btn" href={href}><Info size={14} />{action}</a> : undefined}>
      {body}
    </StateCard>
  );
}

/** Gated tab/feature (starter API): who it's for, no upsell pressure. */
export const Locked = ({ what, forWhom, href }: { what: string; forWhom: string; href?: string }) => (
  <NotAvailable title={`${what} is for ${forWhom}`} body="Residents get forecasts, votes and money on every bill and profile." action={href ? "See what residents get" : undefined} href={href} />
);

/* ---------- any other empty state (starter API) ---------- */
const ICONS: Record<string, LucideIcon> = {
  "building-2": Building2, lock: Lock, "map-pin": MapPin, bell: Bell, search: Search, "search-x": SearchX, "check-check": CheckCheck, "calendar-plus": CalendarPlus,
  "triangle-alert": AlertTriangle, "refresh-cw": RefreshCw, "external-link": ExternalLink, info: Info,
};
const Icon = ({ name, size }: { name?: string; size: number }) => { const I = (name && ICONS[name]) || Info; return <I size={size} />; };

export function EmptyState({ icon, title, body, action }: {
  icon: string; title: string; body: string; action?: { label: string; icon?: string; onClick?: () => void; href?: string };
}) {
  return (
    <StateCard icon={<Icon name={icon} size={18} />} title={title} role="status"
      actions={action && (action.href
        ? <a className="state-btn" href={action.href}>{action.icon && <Icon name={action.icon} size={14} />}{action.label}</a>
        : <button className="state-btn" onClick={action.onClick}>{action.icon && <Icon name={action.icon} size={14} />}{action.label}</button>)}>
      {body}
    </StateCard>
  );
}
